import type { IntentActionType, PayloadRecord } from "./types";
import { isRecordArray, isStringArray } from "./utils";

const asNamedReference = (id: string, map: Map<string, string>): string => map.get(id) ?? id;

const mapIdArray = (value: unknown, map: Map<string, string>): unknown => {
  if (!isStringArray(value)) {
    return value;
  }

  return value.map((id) => asNamedReference(id, map));
};

const humanizeUpdateRecord = (
  record: PayloadRecord,
  serviceNameMap: Map<string, string>,
  categoryNameMap: Map<string, string>
): PayloadRecord => {
  const next: PayloadRecord = { ...record };

  if (typeof next.serviceId === "string") {
    next.serviceId = asNamedReference(next.serviceId, serviceNameMap);
  }
  if (typeof next.categoryId === "string") {
    next.categoryId = asNamedReference(next.categoryId, categoryNameMap);
  }

  return next;
};

type HumanizeOptions = {
  workerNameMap?: Map<string, string>;
  appointmentServiceNameMap?: Map<string, string>;
  customerNameMap?: Map<string, string>;
};

export const humanizePayload = (
  payload: PayloadRecord,
  actionType: IntentActionType,
  serviceNameMap: Map<string, string>,
  categoryNameMap: Map<string, string>,
  options?: HumanizeOptions
): PayloadRecord => {
  const next: PayloadRecord = { ...payload };

  if (typeof next.serviceId === "string") {
    next.serviceId = asNamedReference(next.serviceId, serviceNameMap);
  }
  if (typeof next.categoryId === "string") {
    next.categoryId = asNamedReference(next.categoryId, categoryNameMap);
  }
  if (typeof next.targetCategoryId === "string") {
    next.targetCategoryId = asNamedReference(next.targetCategoryId, categoryNameMap);
  }
  if (typeof next.categoryChange === "string") {
    next.categoryChange = asNamedReference(next.categoryChange, categoryNameMap);
  }

  if (typeof next.workerId === "string" && options?.workerNameMap) {
    next.workerId = asNamedReference(next.workerId, options.workerNameMap);
  }

  if (actionType === "CREATE_DISCOUNT" || actionType === "UPDATE_DISCOUNT") {
    const input = next.input;
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
      return next;
    }

    const inputRecord = input as PayloadRecord;
    const rules = inputRecord.rules;
    if (!Array.isArray(rules)) {
      return next;
    }

    const nextRules = rules.map((rule) => {
      if (typeof rule !== "object" || rule === null || Array.isArray(rule)) return rule;
      const ruleRecord = rule as PayloadRecord;

      const target = ruleRecord.target;
      if (typeof target !== "object" || target === null || Array.isArray(target)) return rule;
      const targetRecord = target as PayloadRecord;

      const serviceIds = targetRecord.serviceIds;
      if (!Array.isArray(serviceIds)) return rule;

      const nextServiceIds = serviceIds.map((serviceId) => {
        if (typeof serviceId !== "string") return serviceId;
        return asNamedReference(serviceId, serviceNameMap);
      });

      return {
        ...ruleRecord,
        target: {
          ...targetRecord,
          serviceIds: nextServiceIds,
        },
      } satisfies PayloadRecord;
    });

    next.input = {
      ...inputRecord,
      rules: nextRules,
    };

    return next;
  }

  if (typeof next.customerId === "string" && options?.customerNameMap) {
    next.customerId = asNamedReference(next.customerId, options.customerNameMap);
  }

  if (typeof next.appointmentServiceId === "string" && options?.appointmentServiceNameMap) {
    next.appointmentServiceId = asNamedReference(
      next.appointmentServiceId,
      options.appointmentServiceNameMap
    );
  }

  next.serviceIds = mapIdArray(next.serviceIds, serviceNameMap);
  next.categoryIds = mapIdArray(next.categoryIds, categoryNameMap);

  if (actionType === "SORT_ITEMS" && isRecordArray(next.items)) {
    const sortType = typeof next.type === "string" ? next.type : "services";
    const itemMap = sortType === "categories" ? categoryNameMap : serviceNameMap;
    next.items = next.items.map((item) => {
      if (typeof item.id !== "string") {
        return item;
      }
      return {
        ...item,
        id: asNamedReference(item.id, itemMap),
      };
    });
  }

  if (actionType === "BATCH_UPDATE_SERVICES" && isRecordArray(next.updates)) {
    next.updates = next.updates.map((update) =>
      humanizeUpdateRecord(update, serviceNameMap, categoryNameMap)
    );
  }

  if (actionType === "BATCH_UPDATE_CATEGORIES" && isRecordArray(next.updates)) {
    next.updates = next.updates.map((update) => {
      if (typeof update.categoryId !== "string") {
        return update;
      }
      return {
        ...update,
        categoryId: asNamedReference(update.categoryId, categoryNameMap),
      };
    });
  }

  if (actionType === "BATCH_CREATE_SERVICES" && isRecordArray(next.services)) {
    next.services = next.services.map((service) => {
      if (typeof service.categoryId !== "string") {
        return service;
      }

      return {
        ...service,
        categoryId: asNamedReference(service.categoryId, categoryNameMap),
      };
    });
  }

  if (actionType === "CREATE_APPOINTMENT" && isRecordArray(next.serviceSelections)) {
    next.serviceSelections = next.serviceSelections.map((selection) => {
      if (typeof selection.serviceId !== "string") {
        return selection;
      }

      return {
        ...selection,
        serviceId: asNamedReference(selection.serviceId, serviceNameMap),
      };
    });
  }

  return next;
};
